import { Link } from 'react-router-dom';
import type { ContractOwner, ContractOwnerStep } from '@/lib/apiClient';
import { Badge, type BadgeTone } from '@/components';
import { fmtDate } from '@/lib/dates';
import { jobHref, OWNER_STEP_LABELS } from './contractsModel';

const STEP_TONE: Record<ContractOwnerStep, BadgeTone> = {
  new: 'neutral',
  received: 'info',
  redline_sent: 'warning',
  approved: 'success',
  completed: 'neutral',
};

const dash = <span className="ct-none">—</span>;
const when = (iso: string | null) => (iso ? fmtDate(iso) : dash);

/**
 * Owner contracts under review. A contract can be here before it has a job, so
 * the Job column may be empty; on a job's own tab that column is not shown.
 */
export default function OwnerContractSection({ rows, canEdit, showJob, onEdit }: {
  rows: ContractOwner[];
  canEdit: boolean;
  showJob: boolean;
  onEdit: (row: ContractOwner) => void;
}) {
  if (rows.length === 0) return null;
  return (
    <section className="ct-section" aria-labelledby="ct-owner-title">
      <header className="ct-section-head">
        <h2 className="ct-section-title" id="ct-owner-title">Owner Contracts</h2>
        <span className="ct-section-count">{rows.length}</span>
      </header>
      <div className="ew-table-scroll">
        <table className="ct-table ct-table--owners">
          <thead>
            <tr>
              <th scope="col">Contract / client</th>
              {showJob && <th scope="col" className="ct-w-job">Job</th>}
              <th scope="col" className="ct-w-person">Sent by</th>
              <th scope="col" className="ct-w-date">Received</th>
              <th scope="col" className="ct-w-date">Redline sent</th>
              <th scope="col" className="ct-w-date">Approved</th>
              <th scope="col" className="ct-w-status">Status</th>
              <th scope="col" className="ct-w-notes">Notes</th>
              {canEdit && <th scope="col" className="ct-w-act"><span className="ct-sr">Actions</span></th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((o) => (
              <tr key={o.id} className={o.step === 'completed' ? 'ct-row--done' : undefined}>
                <td>{o.name}</td>
                {showJob && (
                  <td className="ct-job">
                    {o.projectId !== null && o.jobNumber
                      ? <Link to={jobHref(o.projectId)}>{o.jobNumber}</Link>
                      : dash}
                    {o.projectTitle && <span className="ct-under">{o.projectTitle}</span>}
                  </td>
                )}
                <td>{o.sentBy ?? dash}</td>
                <td className="ct-date">{when(o.receivedOn)}</td>
                <td className="ct-date">{when(o.redlineSentOn)}</td>
                <td className="ct-date">{when(o.approvedOn)}</td>
                <td>
                  <Badge label={OWNER_STEP_LABELS[o.step]} tone={STEP_TONE[o.step]} />
                  {o.step === 'completed' && o.completedOn && (
                    <span className="ct-under">{fmtDate(o.completedOn)}</span>
                  )}
                </td>
                <td className="ct-notes">{o.notes ?? ''}</td>
                {canEdit && (
                  <td>
                    <button
                      type="button"
                      className="ew-btn-outline"
                      onClick={() => onEdit(o)}
                      aria-label={`Edit ${o.name}`}
                    >
                      Edit
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
